/* eslint-disable @typescript-eslint/no-explicit-any */

const SECRET_KEY = import.meta.env.VITE_APP_API_SECRET_KEY;
const API_KEY = import.meta.env.VITE_APP_API_KEY;

const crypto = window.crypto || (window as any).msCrypto;

async function encrypt(payload: any, type?: any) {
	const encoder = new TextEncoder();
	const secretKey = SECRET_KEY;

	let finalPayload = payload;

	if (type === "apikey") {
		const now = new Date();
		const timestamp = now.toISOString();
		const timezoneOffset = now.getTimezoneOffset(); // India = -330

		finalPayload = `${payload}::${timestamp}::${timezoneOffset}`;
	}

	const salt = crypto.getRandomValues(new Uint8Array(16));
	const iv = crypto.getRandomValues(new Uint8Array(12));

	const keyMaterial = await crypto.subtle.importKey(
		"raw",
		encoder.encode(secretKey + String.fromCharCode(...salt)),
		{ name: "PBKDF2" },
		false,
		["deriveKey"],
	);

	const key = await crypto.subtle.deriveKey(
		{
			name: "PBKDF2",
			salt: salt,
			iterations: 100000,
			hash: "SHA-256",
		},
		keyMaterial,
		{ name: "AES-GCM", length: 256 },
		false,
		["encrypt"],
	);

	const encryptedBuffer = await crypto.subtle.encrypt(
		{
			name: "AES-GCM",
			iv: iv,
		},
		key,
		encoder.encode(finalPayload),
	);

	const encryptedBytes = new Uint8Array(encryptedBuffer);
	const authTag = encryptedBytes.slice(-16);
	const ciphertext = encryptedBytes.slice(0, -16);

	const combined = new Uint8Array([...salt, ...iv, ...ciphertext, ...authTag]);

	return btoa(String.fromCharCode(...combined));
}

async function decrypt(encryptedBase64: string) {
	const buffer = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));
	const salt = buffer.slice(0, 16);
	const iv = buffer.slice(16, 28);
	const ciphertextWithTag = buffer.slice(28);

	const secretKey = SECRET_KEY;

	const keyMaterial = await crypto.subtle.importKey(
		"raw",
		new TextEncoder().encode(secretKey + String.fromCharCode(...salt)),
		{ name: "PBKDF2" },
		false,
		["deriveKey"],
	);

	const key = await crypto.subtle.deriveKey(
		{
			name: "PBKDF2",
			salt: salt,
			iterations: 100000,
			hash: "SHA-256",
		},
		keyMaterial,
		{ name: "AES-GCM", length: 256 },
		false,
		["decrypt"],
	);

	const decipher = crypto.subtle.decrypt(
		{
			name: "AES-GCM",
			iv: iv,
		},
		key,
		ciphertextWithTag,
	);

	const decryptedBuffer = await decipher;
	const decrypted = new TextDecoder("utf8").decode(decryptedBuffer);

	if (decrypted.startsWith('"') && decrypted.endsWith('"')) {
		return decrypted.slice(1, -1);
	}

	return decrypted;
}

const IV_LENGTH = 12;
const AUTH_TAG_LENGTH = 16;
const MAX_TOKEN_BYTES = 1024;

function toBase64Url(bytes: Uint8Array): string {
	let bin = "";
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	const b64 = btoa(bin)
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/g, "");
	return b64;
}

function fromBase64Url(b64url: string): Uint8Array {
	if (typeof b64url !== "string") {
		console.error("fromBase64Url: expected string, got", typeof b64url);
		return new Uint8Array(0);
	}
	const b64 =
		b64url.replace(/-/g, "+").replace(/_/g, "/") +
		"=".repeat((4 - (b64url.length % 4 || 4)) % 4);
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

async function getAesGcmKey(passphrase: string): Promise<CryptoKey> {
	if (!passphrase) throw new Error("Missing secret");
	const enc = new TextEncoder();
	const hash = await crypto.subtle.digest("SHA-256", enc.encode(passphrase));
	return crypto.subtle.importKey("raw", hash, { name: "AES-GCM" }, false, [
		"encrypt",
		"decrypt",
	]);
}

async function encryptId(
	plain: string,
	secret = import.meta.env.VITE_APP_API_SECRET_KEY,
): Promise<string> {
	const key = await getAesGcmKey(secret);
	const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
	const data = new TextEncoder().encode(plain);

	const ctWithTagBuf = await crypto.subtle.encrypt(
		{ name: "AES-GCM", iv },
		key,
		data,
	);
	const ctWithTag = new Uint8Array(ctWithTagBuf);

	const tag = ctWithTag.slice(ctWithTag.length - AUTH_TAG_LENGTH);
	const ct = ctWithTag.slice(0, ctWithTag.length - AUTH_TAG_LENGTH);

	const packed = new Uint8Array(IV_LENGTH + AUTH_TAG_LENGTH + ct.length);
	packed.set(iv, 0);
	packed.set(tag, IV_LENGTH);
	packed.set(ct, IV_LENGTH + AUTH_TAG_LENGTH);

	return toBase64Url(packed);
}

async function decryptId(
	encryptedBase64: string,
	secret = import.meta.env.VITE_APP_API_SECRET_KEY,
): Promise<string> {
	if (!encryptedBase64) throw new Error("Invalid token");

	const packed = fromBase64Url(encryptedBase64);

	if (packed.length > MAX_TOKEN_BYTES) throw new Error("Token too large");
	if (packed.length < IV_LENGTH + AUTH_TAG_LENGTH + 1)
		throw new Error("Token too short");

	const iv = packed.slice(0, IV_LENGTH);
	const tag = packed.slice(IV_LENGTH, IV_LENGTH + AUTH_TAG_LENGTH);
	const ct = packed.slice(IV_LENGTH + AUTH_TAG_LENGTH);

	const ctWithTag = new Uint8Array(ct.length + tag.length);
	ctWithTag.set(ct, 0);
	ctWithTag.set(tag, ct.length);

	const key = await getAesGcmKey(secret);
	const plainBuf = await crypto.subtle.decrypt(
		{ name: "AES-GCM", iv, tagLength: 128 },
		key,
		ctWithTag,
	);

	return new TextDecoder().decode(plainBuf);
}

export { API_KEY, decrypt, decryptId, encrypt, encryptId, SECRET_KEY };
